import type { AnswerPanel } from "@/lib/order-assistant/conversation";
import { formatMoney } from "@/lib/order-assistant/format";
import { formatOrderDate } from "@/pages/orders/order-history-utils";

import { AssistantMarkdown } from "./AssistantMarkdown";

type Raw = Record<string, unknown>;

const isRecord = (value: unknown): value is Raw =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const text = (value: unknown): string =>
  typeof value === "string" ? value : "";

function GuidePanel({ payload }: { payload: unknown }) {
  if (!isRecord(payload)) return null;
  const sections = (Array.isArray(payload.sections) ? payload.sections : [])
    .filter(isRecord)
    .map((section) => ({
      heading: text(section.heading),
      body: text(section.body)
    }))
    .filter((section) => section.heading || section.body);
  const title = text(payload.title);
  if (!title && sections.length === 0) return null;

  return (
    <section className="border-ui-border-base bg-ui-bg-subtle space-y-2 rounded-lg border p-3">
      {title ? (
        <h3 className="text-ui-fg-base text-sm font-semibold">{title}</h3>
      ) : null}
      {sections.map((section, index) => (
        <div key={index} className="space-y-1">
          {section.heading ? (
            <h4 className="text-ui-fg-base text-sm font-medium">
              {section.heading}
            </h4>
          ) : null}
          {section.body ? (
            <AssistantMarkdown
              text={section.body}
              className="text-ui-fg-subtle text-sm"
            />
          ) : null}
        </div>
      ))}
    </section>
  );
}

function OrderStatusPanel({ payload }: { payload: unknown }) {
  if (!isRecord(payload)) return null;
  const order = isRecord(payload.order) ? payload.order : null;
  const items = (order && Array.isArray(order.items) ? order.items : []).filter(
    isRecord
  );
  const total =
    order &&
    typeof order.total === "number" &&
    typeof order.currency === "string"
      ? formatMoney(order.total, order.currency)
      : null;
  const placed =
    order && typeof order.placed_at === "string"
      ? new Date(order.placed_at)
      : null;

  return (
    <section className="border-ui-border-base bg-ui-bg-subtle space-y-2 rounded-lg border p-3">
      <h3 className="text-ui-fg-base text-sm font-semibold">
        {text(payload.order_id)}
        {order && text(order.status) ? (
          <span className="text-ui-fg-subtle ml-2 text-xs font-normal">
            {text(order.status).replaceAll("_", " ")}
          </span>
        ) : null}
      </h3>
      {text(payload.summary) ? (
        <p className="text-ui-fg-base text-sm">{text(payload.summary)}</p>
      ) : null}
      {text(payload.next_step) ? (
        <p className="text-ui-fg-subtle text-sm">{text(payload.next_step)}</p>
      ) : null}
      <p className="text-ui-fg-subtle text-xs">
        {[
          order ? text(order.seller) : "",
          placed && !Number.isNaN(placed.getTime())
            ? formatOrderDate(placed)
            : "",
          total ?? ""
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {items.length > 0 ? (
        <ul className="text-ui-fg-base space-y-0.5 text-sm">
          {items.map((item, index) => (
            <li key={index}>
              {typeof item.quantity === "number" ? `${item.quantity} × ` : ""}
              {text(item.title)}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** The `guide` and `order_status` components of a turn. The tracking link of an
 *  order is deliberately not rendered: nothing in the drawer links out. */
export function PanelView({ panel }: { panel: AnswerPanel }) {
  return panel.component === "guide" ? (
    <GuidePanel payload={panel.payload} />
  ) : (
    <OrderStatusPanel payload={panel.payload} />
  );
}
